import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import JourneySection from '../components/sections/JourneySection';
import QuoteStrip from '../components/sections/QuoteStrip';
import FounderPilotCard from '../components/sections/FounderPilotCard';
import PilotApplicationForm from '../components/forms/PilotApplicationForm';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { usePageMeta } from '../lib/usePageMeta';
import { CheckCircle, Shield, Users, Sparkles, ArrowRight, ArrowDown } from 'lucide-react';

const EARLY_ACCESS_INCLUDES = [
  'Browser-based access — nothing to install',
  'No charge during the pilot period',
  'Organise a current or recent housing issue',
  'Access to available early features',
  'Product updates as new features arrive',
  'Optional feedback — only if you want to',
];

const COHORT_INCLUDES = [
  'Founder-supported onboarding',
  'Scheduled check-ins across the eight weeks',
  'Closer help using ResiAX on a real issue',
  'Opportunities to test and influence features',
  'Structured feedback participation',
];

const WHO_ITS_FOR = [
  'Social renters',
  'Private renters',
  'Shared owners',
  'Leaseholders',
  'Adults with a current or recent repair, damp, safety, complaint, communication or related housing issue',
  'Residents who want a clearer way to organise information',
  'People interested in giving optional feedback',
];

const AFTER_REGISTRATION = [
  'Submit the short registration below.',
  'Continue securely to the ResiAX app.',
  'Create or connect your resident account.',
  'Start the early-access onboarding journey.',
  'Tell us if you\u2019re interested in the supported cohort.',
  'Receive separate confirmation if selected for the supported cohort.',
];

const TRUST_POINTS = [
  'Registering will not affect your tenancy.',
  'Your landlord is not contacted without your permission.',
  'You choose what to share and who sees it.',
  'Sponsorship never grants access to your private records.',
  'Your account and records remain yours.',
  'Private details are never exposed in public links.',
  'ResiAX is not an emergency service or legal representative.',
];

const FAQS = [
  { q: 'Is registration free?', a: 'Yes. Early access is free for residents during the pilot period, and there is nothing to install — ResiAX works in your web browser.' },
  { q: 'Do I need my landlord to take part?', a: 'No. You can register and use ResiAX independently. Your landlord is not contacted without your permission.' },
  { q: 'Does registering mean I\u2019m in the supported cohort?', a: 'No. Early access is open to suitable adult residents. The supported eight-week cohort is a smaller group (around 10–20 residents) that we confirm separately — we\u2019ll contact you about it after you register.' },
  { q: 'Can I use ResiAX if I\u2019m not selected for the supported cohort?', a: 'Yes. Early access continues regardless. The cohort simply adds closer, founder-supported onboarding and structured feedback for a small group.' },
  { q: 'Will my landlord know I registered?', a: 'No. Registration is confidential, and nothing is shared with your landlord unless you choose to share it.' },
  { q: 'What information can I share?', a: 'You decide. You can share selected updates, evidence or timelines with a landlord, housing officer, agent, advice worker or representative — never your whole account by default.' },
  { q: 'Does ResiAX replace my landlord\u2019s repairs portal?', a: 'Not necessarily. You should still report issues through your landlord\u2019s official process where one exists. ResiAX keeps your own organised record alongside it.' },
  { q: 'Can ResiAX guarantee my issue will be resolved?', a: 'No. ResiAX strengthens your record and communication, but it can\u2019t guarantee your landlord will resolve the issue, and it isn\u2019t an emergency service or legal representation.' },
  { q: 'What happens after the early-access period?', a: 'We\u2019ll let you know well before anything changes. Your records remain yours, and free resident participation applies throughout the pilot period.' },
  { q: 'How can I leave or ask for my data to be deleted?', a: 'Email us at any time and we\u2019ll remove your registration and associated data. Leaving is always your choice, with no impact on your tenancy.' },
];

const ResidentPilotPage = () => {
  usePageMeta(
    'Resident Early Access Pilot — ResiAX',
    'Register for the ResiAX resident early access pilot: start using ResiAX free in your browser, organise a real housing issue, and help shape what we build.'
  );

  React.useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const scrollToRegister = () => {
    const el = document.querySelector('#register');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main>
        {/* A. Pilot hero */}
        <section className="pt-32 pb-14 bg-white" data-testid="pilot-page-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#B5541B]" />
              <span className="text-sm font-semibold text-[#B5541B]">Early access is open</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1F1E1E] leading-tight mb-5">
              Resident Early Access Pilot
            </h1>
            <p className="text-base md:text-lg text-[#3B3535]/70 leading-relaxed max-w-2xl mx-auto mb-8">
              Register to start using ResiAX and help shape a clearer way to organise housing
              issues, evidence and communication.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                onClick={scrollToRegister}
                data-testid="pilot-page-primary-cta"
                className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-5 sm:h-auto px-8 py-6 text-sm sm:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group max-w-full whitespace-normal h-auto"
              >
                Register for the resident early access pilot
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                data-testid="pilot-page-secondary-cta"
                onClick={() => document.querySelector('#participation-levels')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-[#B5541B]/40 text-[#B5541B] hover:bg-[#B5541B]/10 font-semibold h-auto px-6 py-6 text-base rounded-full transition-all duration-300"
              >
                See what early access includes
                <ArrowDown className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* B. Two participation levels */}
        <section id="participation-levels" className="py-16 bg-[#FFF8F5] scroll-mt-20" data-testid="participation-levels">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] text-center mb-3">Two ways to take part</h2>
            <p className="text-base text-[#3B3535]/70 text-center max-w-2xl mx-auto mb-10">
              Early access is open. Structured pilot support is limited.
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-lg" data-testid="early-access-card">
                <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-3 py-1.5 rounded-full mb-5">
                  <Sparkles className="w-4 h-4 text-[#B5541B]" />
                  <span className="text-xs font-bold uppercase tracking-wide text-[#B5541B]">Open to register</span>
                </div>
                <h3 className="text-xl font-bold text-[#1F1E1E] mb-2">Resident early access</h3>
                <p className="text-sm text-[#3B3535]/70 leading-relaxed mb-5">
                  Open registration that lets you create or connect an account and start using ResiAX.
                </p>
                <ul className="space-y-2.5">
                  {EARLY_ACCESS_INCLUDES.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[#3B3535]/85">
                      <CheckCircle className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#1F1E1E] rounded-3xl p-8 shadow-lg" data-testid="cohort-card">
                <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full mb-5">
                  <Users className="w-4 h-4 text-[#D4722F]" />
                  <span className="text-xs font-bold uppercase tracking-wide text-[#D4722F]">Limited · confirmed separately</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Supported eight-week cohort</h3>
                <p className="text-sm text-white/70 leading-relaxed mb-5">
                  A smaller group of approximately 10–20 residents who receive:
                </p>
                <ul className="space-y-2.5">
                  {COHORT_INCLUDES.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/85">
                      <CheckCircle className="w-4 h-4 text-[#D4722F] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-center text-sm text-[#3B3535]/70 mt-8 max-w-2xl mx-auto" data-testid="cohort-distinction-note">
              Registering for early access does not automatically confirm a place in the supported
              cohort. We will contact residents separately about the supported cohort.
            </p>
          </div>
        </section>

        {/* C. Who it's for */}
        <section className="py-16 bg-white" data-testid="who-its-for">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-6">Who it&apos;s for</h2>
            <div className="flex flex-wrap gap-2.5">
              {WHO_ITS_FOR.map((item, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-[#FFF8F5] border border-[#B5541B]/15 rounded-full px-4 py-2 text-sm text-[#3B3535]/85">
                  <CheckCircle className="w-3.5 h-3.5 text-[#B5541B]" />
                  {item}
                </span>
              ))}
            </div>
            <p className="text-sm text-[#3B3535]/60 mt-5">
              You don&apos;t need to prove how serious your situation is — if a clearer record would help you, you&apos;re welcome.
            </p>
          </div>
        </section>

        {/* D. What residents can do — reuse the four-part journey */}
        <JourneySection />

        {/* E. What happens after registration */}
        <section className="py-16 bg-[#FFF8F5]" data-testid="after-registration">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-8">What happens after you register</h2>
            <ol className="space-y-4">
              {AFTER_REGISTRATION.map((step, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#B5541B] text-white text-sm font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <p className="text-base text-[#3B3535]/85 leading-relaxed pt-1">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* F. Trust and control */}
        <section className="py-16 bg-white" data-testid="pilot-trust">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#F0F9F0] rounded-3xl p-8 border border-green-200/50">
              <h2 className="text-2xl font-bold text-[#1F1E1E] mb-5 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </span>
                Trust and control
              </h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {TRUST_POINTS.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-green-800/90 leading-relaxed">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* F2. Founder-supported pilot */}
        <FounderPilotCard />

        <QuoteStrip />

        {/* G. FAQs */}
        <section className="py-16 bg-[#FFF8F5]" data-testid="pilot-faqs">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E] mb-8 text-center">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-gray-100 px-6 shadow-sm">
                  <AccordionTrigger className="text-left text-sm font-semibold text-[#1F1E1E] hover:no-underline py-4" data-testid={`pilot-faq-${i}`}>
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#3B3535]/75 leading-relaxed pb-4">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* H. Registration form */}
        <section id="register" className="py-16 lg:py-24 bg-white scroll-mt-20" data-testid="pilot-register-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-4">
                Register for the resident early access pilot
              </h2>
              <p className="text-base text-[#3B3535]/70 max-w-xl mx-auto">
                A short registration — a couple of minutes at most. You can continue straight into
                ResiAX afterwards.
              </p>
            </div>
            <PilotApplicationForm />
            <p className="text-center text-sm text-[#3B3535]/60 mt-6">
              Already registered but your link expired?{' '}
              <a href="/resident-pilot/recover" data-testid="recover-link" className="font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors">
                Request a fresh link
              </a>
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default ResidentPilotPage;
